"use client"

import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Trophy, Upload, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/lib/i18n/language-provider";
import { getApiUrl } from "@/lib/api-url";

interface SnakeScoreSubmitProps {
  score: number;
  totalPanels: number;
  gridCols: number;
  gridRows: number;
  gridSize: number;
  gridWidth: number;
  gridHeight: number;
  isGameOver: boolean;
  isVictory: boolean;
  onScoreSubmitted?: () => void;
}

export function SnakeScoreSubmit({ 
  score, 
  totalPanels, 
  gridCols,
  gridRows,
  gridSize, 
  gridWidth, 
  gridHeight, 
  isGameOver, 
  isVictory,
  onScoreSubmitted 
}: SnakeScoreSubmitProps) {
  const { t } = useLanguage();
  const [playerName, setPlayerName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleSubmit = async () => {
    if (!playerName.trim()) {
      toast.error(t('snake.playerName'));
      return;
    }

    if (score <= 0) {
      toast.error(t('toast.invalidScore'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(getApiUrl('/api/snake/score'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playerName: playerName.trim(),
          score,
          totalPanels,
          gridCols,
          gridRows,
          gridSize,
          gridWidth,
          gridHeight
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la soumission');
      }

      const result = await response.json();
      
      setIsSubmitted(true);
      
      if (result.isNewPersonalRecord) {
        toast.success(t('snake.newPersonalRecord').replace('{score}', score.toString()));
      } else {
        toast.success(t('snake.scoreSubmitted').replace('{score}', score.toString()));
      }

      // Fermer la dialog après 2 secondes
      setTimeout(() => {
        setIsDialogOpen(false);
        if (onScoreSubmitted) {
          onScoreSubmitted();
        }
      }, 2000);

    } catch (error) {
      // console.error('Erreur lors de la soumission du score:', error);
      toast.error(t('snake.submissionError') || 'Erreur lors de la soumission');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('fr-FR').format(num);
  };

  // Ne pas afficher le bouton si le score est 0
  if (score <= 0 || (!isGameOver && !isVictory)) {
    return null;
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="default" 
          size="sm"
          className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600"
        >
          <Upload className="w-4 h-4 mr-2" />
          {t('snake.submitScore')}
        </Button>
      </DialogTrigger>
      
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isVictory ? (
              <>
                <Trophy className="w-5 h-5 text-yellow-500" />
                {t('snake.victory')}
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-orange-500" />
                {t('snake.gameOver')}
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isVictory 
              ? t('snake.victoryMessage')
              : t('snake.gameOverMessage')
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Résumé du score */}
          <div className="bg-muted rounded-lg p-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">{t('snake.finalScore')}</span>
              <Badge variant="secondary" className="text-lg font-bold px-3 py-1">
                {formatNumber(score)} {t('snake.points')}
              </Badge>
            </div>
            <div className="flex justify-between items-center text-sm text-muted-foreground">
              <span>{t('snake.totalPanels')}</span>
              <span>{formatNumber(totalPanels)}</span>
            </div>
            <div className="flex justify-between items-center text-sm text-muted-foreground">
              <span>{t('snake.grid')}</span>
              <span>{gridCols} × {gridRows}</span>
            </div>
          </div>

          {!isSubmitted ? (
            <>
              {/* Saisie du nom */}
              <div className="space-y-2">
                <Label htmlFor="playerName">{t('snake.playerName')}</Label>
                <Input
                  id="playerName"
                  placeholder={t('snake.enterName')}
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  maxLength={50}
                  disabled={isSubmitting}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isSubmitting) {
                      handleSubmit();
                    }
                  }}
                />
              </div>

              {/* Boutons */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !playerName.trim()}
                  className="flex-1"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      {t('snake.saving')}
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {t('snake.save')}
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  disabled={isSubmitting}
                >
                  {t('snake.cancel')}
                </Button>
              </div>
            </>
          ) : (
            /* Confirmation de soumission */
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
              <h3 className="text-lg font-semibold text-green-700 dark:text-green-400 mb-2">
                {t('snake.scoreRecorded')}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('snake.scoreRecordedMessage')}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
